import type { DealEnum, Sheet as GQLItem } from '../../../generated/api.graphql';
import { take } from '../../../util';
import * as User from '../User/User';
import * as Agent from './Agent';
import * as ExpenseCategory from './ExpenseCategory';
import * as List from './List';
import * as OfferSheetContact from './OfferSheetContact';
import * as OfferTicket from './OfferTicket';
import * as SheetVersion from './SheetVersion';
import * as Summary from './Summary';

/**
 * Nullability mirrors the schema, as it does everywhere else under `App/`: only `id` and
 * `offerDate` are non-null on AppSchema, so only those two are required here. The admin store's
 * local `OfferSheet` interface is far stricter and stays that way for now -- the offer-sheet
 * components lean on it -- which is why the store converts at the boundary rather than adopting
 * this type outright.
 *
 * Two fields the store's interface carries are deliberately absent: `htmlContent` and
 * `pdfDownloadUrl` do not exist on AppSchema's `Sheet`. `Api.fetchOfferSheetRendering` supplies
 * them from the legacy schema until they do.
 *
 * `summary` is a single object. The store declares `summary?: Array<Summary>`, which neither
 * schema nor the runtime has ever agreed with; that mis-declaration is not reproduced here.
 */
export type Sheet = {
  id: number;
  offerDate: string;
  name?: string;
  offerExpirationDate?: string;
  artistName?: string;
  attendeeCount?: number;
  billing?: string;
  dealArtistGuarantee?: number;
  dealGborPercentage?: number;
  dealType?: DealEnum;
  doorTime?: string;
  endTime?: string;
  eventGroupId?: number;
  exclusivity?: string;
  headlinerGuarantee?: number;
  listCount?: number;
  lockedAt?: string;
  merch?: string;
  minimumAge?: number;
  note?: string;
  performanceDatesDescription?: string;
  promoterName?: string;
  promoterProfitPercentage?: number;
  startTime?: string;
  support?: string;
  supportGuarantee?: number;
  taxPercentage?: number;
  ticketCount?: number;
  venueAddress?: string;
  venueLogo?: string;
  venueName?: string;
  venueTimezone?: string;
  agent?: Agent.Agent;
  contacts?: OfferSheetContact.OfferSheetContact[];
  expenseCategories?: ExpenseCategory.ExpenseCategory[];
  lists?: List.List[];
  lockedBy?: User.User;
  summary?: Summary.Summary;
  tickets?: OfferTicket.OfferTicket[];
  versions?: SheetVersion.SheetVersion[];
};

export const fromGql = (item: GQLItem): Sheet => ({
  ...take<Sheet, GQLItem>(
    item,
    'id',
    'offerDate',
    'name',
    'offerExpirationDate',
    'artistName',
    'attendeeCount',
    'billing',
    'dealArtistGuarantee',
    'dealGborPercentage',
    'dealType',
    'doorTime',
    'endTime',
    'eventGroupId',
    'exclusivity',
    'headlinerGuarantee',
    'listCount',
    'lockedAt',
    'merch',
    'minimumAge',
    'note',
    'performanceDatesDescription',
    'promoterName',
    'promoterProfitPercentage',
    'startTime',
    'support',
    'supportGuarantee',
    'taxPercentage',
    'ticketCount',
    'venueAddress',
    'venueLogo',
    'venueName',
    'venueTimezone',
  ),
  agent: item.agent ? Agent.fromGql(item.agent) : undefined,
  contacts: item.contacts ? item.contacts.map(OfferSheetContact.fromGql) : [],
  expenseCategories: item.expenseCategories ? item.expenseCategories.map(ExpenseCategory.fromGql) : [],
  lists: item.lists ? item.lists.map(List.fromGql) : [],
  lockedBy: item.lockedBy ? User.fromGql(item.lockedBy) : undefined,
  summary: item.summary ? Summary.fromGql(item.summary) : undefined,
  tickets: item.tickets ? item.tickets.map(OfferTicket.fromGql) : [],
  versions: item.versions ? item.versions.map(SheetVersion.fromGql) : [],
});
